import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import Typography from "@/components/ui/typography";
import CancelSubscriptionDialog from "@/features/founders/components/CancelSubscriptionDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";

const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const DangerZoneSection = () => {
  const { user } = useCurrentUser();
  const [open, setOpen] = useState(false);

  // Server-driven cancellation state (STA-743). Populated by the
  // /licensees/cancel-subscription mutation, which invalidates the ["me"]
  // query so this re-renders with isCancelled=true post-mutation.
  const isCancelled = !!user?.subscriptionCancellationRequestedAt;

  const subscriptionEndDate = useMemo(() => {
    // Prefer `subscriptionEndsAt` once cancellation is requested (matches the
    // offboarding cron). Otherwise use `subscriptionValidUntil`, set to
    // onboarding + 1y on the server — the canonical renewal date.
    const iso = user?.subscriptionEndsAt || user?.subscriptionValidUntil;
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [user?.subscriptionEndsAt, user?.subscriptionValidUntil]);

  if (!subscriptionEndDate) return null;

  const endDateLabel = formatDate(subscriptionEndDate);

  return (
    <>
      <div className="bg-[#f9f9f9] rounded-2xl p-6 space-y-4">
        <div className="space-y-1">
          <Typography size="l2" className="font-semibold text-[#494949]">
            Danger Zone
          </Typography>
          {isCancelled ? (
            <Typography size="b2" className="text-[#767677]">
              Your subscription will end on{" "}
              <span className="font-semibold text-[#494949]">
                {endDateLabel}
              </span>
              . You will not be charged for another year.
            </Typography>
          ) : (
            <Typography size="b2" className="text-[#767677]">
              Your subscription renews on{" "}
              <span className="font-semibold text-[#494949]">
                {endDateLabel}
              </span>
              .
            </Typography>
          )}
        </div>
        {!isCancelled && (
          <Button
            type="button"
            className="leading-10"
            onClick={() => setOpen(true)}
          >
            Cancel Subscription
          </Button>
        )}
      </div>

      <CancelSubscriptionDialog
        open={open}
        onOpenChange={setOpen}
        endDateLabel={endDateLabel}
      />
    </>
  );
};

export default DangerZoneSection;
